/**
 * Port of spatie/laravel-permission's WildcardPermission matching (the engine
 * behind the backend's $user->can() with enable_wildcard_permission on).
 *
 * Semantics are Apache-Shiro style and pinned by the shared fixture file
 * (permission-matching.fixtures.json, sourced from incept-api's
 * tests/fixtures/permission-matching.json):
 * - granted names build a trie; a grant terminating at a node implies every
 *   deeper requested key (prefix-implies: 'docs' implies 'docs.view')
 * - '*' matches any single segment, and everything beneath when a grant
 *   terminates behind it ('*' alone implies every key)
 * - ',' separates alternatives within one segment ('users.view,create')
 *
 * Do NOT change behaviour here without changing the backend and the fixture
 * file together; the fixtures run against both implementations.
 */

const PART_DELIMITER = '.';
const SUBPART_DELIMITER = ',';
const WILDCARD = '*';

/** The empty-string key marks "a grant terminates at this node". */
const TERMINAL = '';

export type PermissionTrie = { [segment: string]: PermissionTrie };

// Trie construction mutates the node being built by design.
/* eslint-disable no-param-reassign */
const insert = (node: PermissionTrie, parts: readonly string[]): void => {
  if (parts.length === 0) {
    node[TERMINAL] = node[TERMINAL] ?? {};
    return;
  }

  const [part, ...rest] = parts;

  part.split(SUBPART_DELIMITER).forEach((subPart) => {
    if (subPart === '') {
      return; // malformed grant segment; the backend rejects these at seed time
    }
    node[subPart] = node[subPart] ?? {};
    insert(node[subPart], rest);
  });
};
/* eslint-enable no-param-reassign */

export const buildPermissionIndex = (
  granted: readonly string[],
): PermissionTrie => {
  const index: PermissionTrie = {};
  granted.forEach((name) => insert(index, name.split(PART_DELIMITER)));
  return index;
};

const check = (parts: readonly string[], index: PermissionTrie): boolean => {
  if (TERMINAL in index) {
    return true;
  }

  if (parts.length === 0) {
    return false;
  }

  const [first, ...rest] = parts;

  if (first in index && check(rest, index[first])) {
    return true;
  }

  if (WILDCARD in index) {
    return check(rest, index[WILDCARD]);
  }

  return false;
};

export const indexMatches = (
  index: PermissionTrie,
  requested: string,
): boolean => check(requested.split(PART_DELIMITER), index);

/**
 * One-shot convenience for a single check. Building the trie is cheap, but
 * callers checking many keys against the same grants (useCan) should build
 * the index once via buildPermissionIndex + indexMatches.
 */
export const permissionMatches = (
  granted: readonly string[],
  requested: string,
): boolean => indexMatches(buildPermissionIndex(granted), requested);
